import React, { createContext, useContext, useEffect, useState } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import type { User as UserProfile } from '../lib/database.types';
import toast from 'react-hot-toast'; 

interface AuthContextType {
  user: User | null;
  userProfile: UserProfile | null;
  loading: boolean;
  error: string | null;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signUp: (email: string, password: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUserProfile = async (userId: string, email?: string) => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (error) {
        console.error('Error fetching user profile:', error);
        return null;
      }

      if (!data && email) {
        const { data: newProfile, error: insertError } = await supabase
          .from('users')
          .insert([{ id: userId, email }])
          .select()
          .maybeSingle();

        if (insertError) {
          console.error('Error creating user profile:', insertError);
          return null;
        }

        return newProfile;
      }

      return data;
    } catch (err) {
      console.error('Profile error:', err);
      return null;
    }
  };

  const clearError = () => {
    setError(null);
  };

  useEffect(() => {
    const fetchSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();

        if (session?.user) {
          setUser(session.user);
          const profile = await fetchUserProfile(session.user.id, session.user.email);
          setUserProfile(profile);
        }
      } catch (err) {
        console.error('Session error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (session?.user) {
          setUser(session.user);
          const profile = await fetchUserProfile(session.user.id, session.user.email);
          setUserProfile(profile);
        } else {
          setUser(null);
          setUserProfile(null);
        }
        setLoading(false);
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      setError(null);

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        toast.error(error.message);
        return { error: error.message };
      }

      toast.success('Login realizado com sucesso!');

      if (data.user) {
        const profile = await fetchUserProfile(data.user.id, data.user.email);
        setUserProfile(profile);
      }

      return { error: null };
    } catch (err: any) {
      const errorMessage = err.message || 'Erro ao fazer login';
      toast.error(errorMessage);
      setError(errorMessage);
      return { error: errorMessage };
    }
  };

  const signUp = async (email: string, password: string) => {
    try {
      setError(null);

      const { data, error } = await supabase.auth.signUp({
        email,
        password
      });

      if (error) {
        toast.error(error.message);
        return { error: error.message };
      }

      if (data.user) {
        await fetchUserProfile(data.user.id, data.user.email);
      }

      toast.success('Conta criada com sucesso!');
      return { error: null };
    } catch (err: any) {
      const errorMessage = err.message || 'Erro ao criar conta';
      toast.error(errorMessage);
      setError(errorMessage);
      return { error: errorMessage };
    }
  };

  const signOut = async () => {
    try {
      setError(null);
      await supabase.auth.signOut();
      setUserProfile(null);
      toast.success('Logout realizado com sucesso');
    } catch (err: any) {
      toast.error(err.message || 'Erro ao fazer logout');
      setError(err.message || 'Failed to sign out');
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      userProfile,
      loading,
      error,
      signIn,
      signUp,
      signOut,
      clearError
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};